import { z } from 'zod';

import {
  MODEL_PROFILES,
  RUN_STATUSES,
  RUN_STEP_KINDS,
  RUN_STEP_STATUSES,
  RUN_TRIGGERS,
} from '../run.js';

/**
 * Runs as the SPA and the public API see them - AGT-002.
 *
 * Paths are fixed by `docs/architecture/aws/api-contracts-and-events.md` §25.1
 * and `public-api-and-mcp.md`: `POST /v1/runs`, `GET /v1/runs/{id}`. The
 * workspace travels in the header, never in the path - ADR-030 - which is why
 * `workspaceId` is absent from every shape below.
 *
 * **`POST` answers 202, not 201.** A run is accepted and then happens; the
 * caller is being told where to look, not handed a finished thing. That is also
 * what makes the polling contract in `use-messages.ts` possible without the
 * route pretending to be synchronous.
 *
 * The vocabularies are imported rather than restated, for the reason
 * `http/employee.ts` gives: two definitions drift the first time only one is
 * edited, and the drift is silent.
 */

/**
 * The database puts no ceiling on this column. The API does - an unbounded
 * string from a user-supplied field is an input-validation gap even when the
 * column would hold it. Matches `MESSAGE_TEXT_MAX`, because for a `CHAT` run
 * this is the message.
 */
export const RUN_INSTRUCTION_MAX = 8000;

/**
 * What a caller may ask for.
 *
 * **No `status`, no `modelProfile`, no snapshots.** A run starts `QUEUED` by
 * construction - `RunRepository.create` writes it rather than accepting it -
 * and the instruction snapshot and the model profile are decisions the runtime
 * makes from the employee. A caller able to name them is a caller able to start
 * a run that claims to be half finished, or one that quietly picks the
 * expensive model.
 *
 * `trigger` is accepted but only `CHAT` is reachable today: `SCHEDULE` needs
 * EventBridge and `EVENT` needs an ingestion path, and neither exists. It is
 * here because the column is, and refusing the other two at the edge is
 * clearer than pretending the vocabulary is smaller than it is.
 */
export const createRunRequestSchema = z
  .object({
    employeeId: z.uuid().optional(),
    threadId: z.uuid().optional(),
    trigger: z.enum(RUN_TRIGGERS).default('CHAT'),
    instruction: z.string().trim().min(1).max(RUN_INSTRUCTION_MAX),
  })
  .readonly();

export type CreateRunRequest = z.infer<typeof createRunRequestSchema>;

export const runStepSchema = z
  .object({
    stepId: z.string(),
    sequence: z.number().int().nonnegative(),
    kind: z.enum(RUN_STEP_KINDS),
    status: z.enum(RUN_STEP_STATUSES),
    startedAt: z.string(),
    endedAt: z.string().optional(),
    /**
     * A sentence, never a payload. The column is named `summary_redacted` for
     * the same reason: a trace that carried tool output would put a customer's
     * mail through every log this run touches.
     */
    summaryRedacted: z.string().optional(),
    errorCode: z.string().optional(),
  })
  .readonly();

export type RunStepSummary = z.infer<typeof runStepSchema>;

export const runSchema = z
  .object({
    id: z.uuid(),
    employeeId: z.uuid().optional(),
    threadId: z.uuid().optional(),
    initiatedByUserId: z.uuid().optional(),
    trigger: z.enum(RUN_TRIGGERS),
    triggerRef: z.string().optional(),
    status: z.enum(RUN_STATUSES),
    instructionSnapshot: z.string(),
    modelProfile: z.enum(MODEL_PROFILES),
    createdAt: z.string(),
    startedAt: z.string().optional(),
    completedAt: z.string().optional(),
    resultSummary: z.string().optional(),
    errorCode: z.string().optional(),
  })
  .readonly();

export type RunSummary = z.infer<typeof runSchema>;

/**
 * What `POST /v1/runs` answers with, at 202.
 *
 * The whole run rather than an id: the caller has to start polling somewhere,
 * and one round trip that says "QUEUED, here is everything about it" beats two
 * that say "here is an id" and then "here is the run".
 */
export const createRunResponseSchema = z.object({ run: runSchema }).readonly();

export type CreateRunResponse = z.infer<typeof createRunResponseSchema>;

/**
 * What `GET /v1/runs/{id}` answers with.
 *
 * Steps come with the run rather than from `/v1/runs/{id}/events`, which is a
 * later ticket. A poll that needed two requests to answer "what is it doing"
 * would double the cost of the one screen that polls.
 */
/**
 * Every run of one thread, newest first - AGT-008.
 *
 * **The screen needs it to survive a reload.** A run id lived only in React
 * state, so refreshing the page lost track of a run that was still going - and
 * a run parked on an approval was then unreachable, which is D7b's whole
 * premise failing quietly. Steps are not included: the list answers "is
 * anything happening", and the trace is `GET /v1/runs/{id}`.
 */
export const runListResponseSchema = z.object({ runs: z.array(runSchema).readonly() }).readonly();

export type RunListResponse = z.infer<typeof runListResponseSchema>;

export const runResponseSchema = z
  .object({ run: runSchema, steps: z.array(runStepSchema).readonly() })
  .readonly();

export type RunResponse = z.infer<typeof runResponseSchema>;
